import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  Loader2,
  Sparkles,
  Megaphone,
  PowerOff,
  Power,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';

const EMPTY = { title: '', message: '', type: 'update', is_active: true };

export function AdminNotificationsPage() {
  const { admin, loading } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // id being edited, null = create
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      const r = await adminApi.get('/admin/notifications');
      setItems(r.data?.items || []);
    } catch {
      toast.error('Could not load notifications');
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    if (admin) refresh();
  }, [admin]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };
  const openEdit = (n) => {
    setEditing(n.id);
    setForm({ title: n.title, message: n.message, type: n.type, is_active: n.is_active });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await adminApi.put(`/admin/notifications/${editing}`, form);
        toast.success('Notification updated');
      } else {
        await adminApi.post('/admin/notifications', form);
        toast.success('Notification created');
      }
      setDialogOpen(false);
      await refresh();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (n) => {
    if (!window.confirm(`Delete "${n.title}"?`)) return;
    try {
      await adminApi.delete(`/admin/notifications/${n.id}`);
      toast.success('Notification deleted');
      await refresh();
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleToggleActive = async (n) => {
    try {
      await adminApi.put(`/admin/notifications/${n.id}`, { is_active: !n.is_active });
      await refresh();
    } catch {
      toast.error('Update failed');
    }
  };

  return (
    <div
      data-testid="admin-notifications-page"
      className="min-h-screen"
      style={{
        background:
          'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)',
      }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800"
              data-testid="admin-notifications-back"
            >
              <ArrowLeft className="w-4 h-4 text-slate-300" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                <Bell className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <div className="text-white font-semibold">Notifications</div>
                <div className="text-xs text-slate-400">
                  Broadcast updates and offers to the login page
                </div>
              </div>
            </div>
          </div>
          <Button
            onClick={openCreate}
            data-testid="admin-notification-create-btn"
            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
          >
            <Plus className="w-4 h-4 mr-2" /> New notification
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {fetchLoading ? (
          <div className="flex items-center gap-2 text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="admin-notifications-empty"
            className="bg-slate-900/70 border border-dashed border-slate-800 rounded-2xl p-10 text-center"
          >
            <Bell className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <h3 className="text-white font-semibold text-lg">No notifications yet</h3>
            <p className="text-slate-400 text-sm mt-1">
              Create your first update or offer to show it on the login page.
            </p>
            <Button
              onClick={openCreate}
              className="mt-5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" /> Create notification
            </Button>
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((n) => {
              const isOffer = n.type === 'offer';
              const TypeIcon = isOffer ? Sparkles : Megaphone;
              return (
                <li
                  key={n.id}
                  data-testid={`admin-notification-row-${n.id}`}
                  className="bg-slate-900/70 border border-slate-800 rounded-xl p-4 flex gap-4 items-start"
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{
                      background: isOffer ? 'rgba(251,191,36,0.12)' : 'rgba(59,130,246,0.12)',
                      color: isOffer ? '#fbbf24' : '#60a5fa',
                    }}
                  >
                    <TypeIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-white font-semibold">{n.title}</h3>
                      <span
                        className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${
                          isOffer
                            ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                            : 'bg-blue-500/15 text-blue-300 border border-blue-500/30'
                        }`}
                      >
                        {n.type}
                      </span>
                      {n.is_active ? (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                          Active
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-700 text-slate-400">
                          Inactive
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-300 mt-1 leading-snug">{n.message}</p>
                  </div>
                  <div className="flex flex-col md:flex-row items-end md:items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleActive(n)}
                      data-testid={`admin-notification-toggle-${n.id}`}
                      className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white"
                    >
                      {n.is_active ? (
                        <>
                          <PowerOff className="w-3.5 h-3.5 mr-1.5" /> Disable
                        </>
                      ) : (
                        <>
                          <Power className="w-3.5 h-3.5 mr-1.5" /> Enable
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(n)}
                      data-testid={`admin-notification-edit-${n.id}`}
                      className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white"
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(n)}
                      data-testid={`admin-notification-delete-${n.id}`}
                      className="border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 hover:text-red-200"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          data-testid="admin-notification-dialog"
          className="bg-slate-900 border-slate-800 text-white"
        >
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit notification' : 'New notification'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                maxLength={120}
                required
                data-testid="admin-notification-form-title"
                className="bg-slate-800/50 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Message</Label>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                maxLength={500}
                rows={4}
                required
                data-testid="admin-notification-form-message"
                className="bg-slate-800/50 border-slate-700 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Type</Label>
                <Select
                  value={form.type}
                  onValueChange={(v) => setForm({ ...form, type: v })}
                >
                  <SelectTrigger
                    data-testid="admin-notification-form-type"
                    className="bg-slate-800/50 border-slate-700 text-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-white">
                    <SelectItem value="update">Update</SelectItem>
                    <SelectItem value="offer">Offer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Status</Label>
                <div className="h-10 flex items-center gap-3 px-3 rounded-md bg-slate-800/50 border border-slate-700">
                  <Switch
                    checked={form.is_active}
                    onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                    data-testid="admin-notification-form-active"
                  />
                  <span className="text-sm text-slate-200">
                    {form.is_active ? 'Active (visible)' : 'Inactive (hidden)'}
                  </span>
                </div>
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white"
              >
                <X className="w-4 h-4 mr-1.5" /> Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid="admin-notification-form-save"
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-1.5" /> {editing ? 'Save changes' : 'Create'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
